import React, { useRef, useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams, useParams } from 'react-router-dom';
import { Upload, Clock, CheckCircle2, CreditCard, Loader2, Info } from 'lucide-react';
import { SubPageLayout } from '@/components/layout/SubPageLayout';
import { FramedAvatar } from '@/components/ui/FramedAvatar';
import { Button } from '@/components/ui/button';
import { useToast } from '@/shared/hooks/use-toast';
import { validateVideoFile, validateVideoDuration, uploadTrainingVideo } from '@/services/videoService';
import { monitoring } from '@/lib/monitoring';
import { SOSSIETY_RED } from '@/styles/brand';
import { format } from 'date-fns';
import { useI18n } from '@/contexts/I18nContext';
import { useCreatePaymentIntent, useRollReviewBreakdown } from '../hooks/useRollReviewPayment';
import { useInstructorPricingQuery } from '../hooks/useInstructorPricingQuery';
import { useInstructorProfileQuery } from '../hooks/useInstructorProfileQuery';
import { DELIVERY_WINDOW_HOURS } from '../utils/reviewWindows';
import { StripePaymentElementWrapper } from '@/components/StripePaymentElementWrapper';
import { rollReviewApi } from '../services/rollReviewApi';
import { useSavedCard } from '@/features/subscription/hooks/useSavedCard';
import { paymentMethodApi } from '@/features/subscription/services/paymentMethodApi';
import { detectTierFromDurationSeconds } from '../domain/rollReviewPricing';
import { TierPricingTable } from '../components/TierPricingTable';
import { useDiscardPendingVideo } from '../hooks/useDiscardPendingVideo';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { emit } from '@/integrations/analytics/emit';
import {
  ROLL_REVIEW_FLOW_STARTED,
  ROLL_REVIEW_INSTRUCTOR_SELECTED,
  PRICING_VIEWED,
  PAYMENT_FAILED_CLIENT,
} from '@/integrations/analytics/events';

interface Instructor {
  id: string;
  full_name: string;
  username?: string;
  profile_picture_url?: string;
  belt_rank?: string;
  completed_reviews?: number;
  avg_response_time?: string;
  price_cents?: number;
  currency?: string;
  turnaround_hours?: number;
}

// 'upload'    → user selects and uploads video
// 'uploading' → uploading in progress
// 'checkout'  → user sees detected price + fills message → clicks Pay → creates payment intent
// 'payment'   → embedded payment element for Stripe payment
// 'success'   → done
type Step = 'upload' | 'uploading' | 'checkout' | 'payment' | 'success';

const MAX_MESSAGE_CHARS = 500;
const DEFAULT_PRICE_CENTS = 1500; // $15.00 fallback
const DEFAULT_CURRENCY = 'USD';
const DEFAULT_TURNAROUND = DELIVERY_WINDOW_HOURS;

const formatBytes = (bytes: number): string => {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const parseBeltColor = (beltRank?: string): string => {
  if (!beltRank) return 'white';
  const rank = beltRank.toLowerCase();
  if (rank.includes('black'))  return 'black';
  if (rank.includes('brown'))  return 'brown';
  if (rank.includes('purple')) return 'purple';
  if (rank.includes('blue'))   return 'blue';
  return 'white';
};

const getInitials = (fullName?: string): string => {
  if (!fullName) return '?';
  return fullName.split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase();
};

const getBeltLabel = (beltRank?: string, t?: any): string => {
  const rank = beltRank?.toLowerCase() ?? '';
  if (!t) return beltRank ?? 'Black Belt';
  if (rank.includes('black'))  return t('roll_review.black_belt');
  if (rank.includes('brown'))  return t('roll_review.brown_belt');
  if (rank.includes('purple')) return t('roll_review.purple_belt');
  if (rank.includes('blue'))   return t('roll_review.blue_belt');
  return t('roll_review.white_belt');
};

const RequestRollReview: React.FC = () => {
  const navigate      = useNavigate();
  const location      = useLocation();
  const [searchParams] = useSearchParams();
  const { instructorId: instructorIdParam } = useParams<{ instructorId: string }>();
  const { toast }     = useToast();
  const { t }         = useI18n();
  const fileInputRef  = useRef<HTMLInputElement>(null);
  const [isDesktop, setIsDesktop] = React.useState(window.innerWidth >= 1024);

  // After Stripe redirects back, location.state is gone — restore from sessionStorage
  const isReturnFlow = !!(searchParams.get('roll_review_id') && searchParams.get('status'));
  const returnStatus = isReturnFlow ? searchParams.get('status') : null; // 'paid' | 'cancelled'
  const returnRollReviewId = isReturnFlow ? searchParams.get('roll_review_id') : null;
  const storedInstructor = React.useMemo<Instructor | undefined>(() => {
    if (!isReturnFlow) return undefined;
    try {
      const raw = sessionStorage.getItem('rr_pending_instructor');
      return raw ? (JSON.parse(raw) as Instructor) : undefined;
    } catch {
      return undefined;
    }
  }, [isReturnFlow]);

  // Fetch instructor by URL param when not passed via navigation state (e.g. direct URL access, E2E tests)
  const stateInstructor: Instructor | undefined = location.state?.instructor ?? storedInstructor;
  const { data: fetchedProfile, isLoading: profileLoading } = useInstructorProfileQuery(
    stateInstructor ? undefined : instructorIdParam,
  );
  const instructor: Instructor | undefined = stateInstructor ?? (fetchedProfile ?? undefined);

  // Handle window resize for responsive layout
  React.useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const turnaroundHours = instructor?.turnaround_hours ?? DEFAULT_TURNAROUND;
  const { pricing } = useInstructorPricingQuery(instructor?.id ?? instructorIdParam);

  const [step, setStep]           = useState<Step>(() =>
    isReturnFlow && returnStatus === 'cancelled' ? 'checkout' : 'upload'
  );
  const [message, setMessage]     = useState(() =>
    isReturnFlow ? sessionStorage.getItem('rr_pending_message') ?? '' : ''
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStartTime, setUploadStartTime] = useState<number | null>(null);
  const [uploadedVideoId, setUploadedVideoId] = useState<string | null>(null);
  // Storage path is required for `discardPendingVideo` cleanup. We persist it
  // to sessionStorage alongside the id so the Stripe redirect doesn't strand
  // an orphan if the user starts over after returning from the gateway.
  const [uploadedVideoPath, setUploadedVideoPath] = useState<string | null>(null);
  const [detectedDurationSec, setDetectedDurationSec] = useState<number | null>(null);
  const [showStartOverDialog, setShowStartOverDialog] = useState(false);
  const discardPendingVideo = useDiscardPendingVideo();
  const [clientSecret, setClientSecret] = useState<string>('');
  const [paymentIntentStripeId, setPaymentIntentStripeId] = useState<string>('');
  const [paymentRecordId, setPaymentRecordId] = useState<string>('');
  const [paymentRollReviewId, setPaymentRollReviewId] = useState<string>('');
  const [paymentMode, setPaymentMode] = useState<'new' | 'saved'>('new');
  const [isPayingWithSavedCard, setIsPayingWithSavedCard] = useState(false);
  // SOS-336 rule 1 — "Save for future purchase" toggle on one-time checkout.
  // Default OFF (opt-in). Only meaningful when paying with a new card; when
  // the user has a saved card, the spec keeps a single card per user, so the
  // toggle is hidden.
  const [saveForFuture, setSaveForFuture] = useState(false);
  const { card: savedCard, isLoading: savedCardLoading } = useSavedCard();

  // Return from Stripe with status=cancelled: fire toast and clear params
  useEffect(() => {
    if (!isReturnFlow || returnStatus !== 'cancelled') return;
    toast({
      title: t('roll_review.payment_cancelled_title'),
      description: t('roll_review.payment_cancelled_body'),
      variant: 'destructive',
    });
    navigate(location.pathname, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Resume flow: check if we have a pending video upload
  useEffect(() => {
    const pendingVideoId = sessionStorage.getItem('rr_pending_video_id');
    const pendingVideoPath = sessionStorage.getItem('rr_pending_video_path');
    const pendingDurationSeconds = sessionStorage.getItem('rr_pending_duration_seconds');
    if (pendingVideoId && pendingDurationSeconds && !paymentRollReviewId) {
      setUploadedVideoId(pendingVideoId);
      if (pendingVideoPath) setUploadedVideoPath(pendingVideoPath);
      const durationSec = Math.round(Number(pendingDurationSeconds));
      setDetectedDurationSec(durationSec);
      setStep('checkout');
    }
  }, []);

  // Derive price from pricing + detected duration (no race vs pricing query).
  // When the resume useEffect runs before TanStack Query resolves, pricing is
  // undefined; deriving here re-renders correctly the moment pricing arrives.
  const derivedTierKey = detectedDurationSec !== null
    ? detectTierFromDurationSeconds(detectedDurationSec)
    : null;
  const derivedTierIdx = derivedTierKey
    ? (derivedTierKey === 'tier_1' ? 0 : derivedTierKey === 'tier_2' ? 1 : derivedTierKey === 'tier_3' ? 2 : 3)
    : null;
  const derivedPriceCents = pricing && derivedTierIdx !== null
    ? (pricing.tiers[derivedTierIdx]?.priceCents ?? 0)
    : 0;
  const derivedCurrency = pricing?.currency ?? DEFAULT_CURRENCY;

  // Analytics: flow start, instructor selection, pricing view. None of these
  // produce a database row (the payments / roll_reviews rows are created at
  // PaymentIntent creation, on the pay click in the next step), so the bus is
  // the source of truth for the pre-pay funnel. Skip the Stripe return-flow
  // remount, which is a continuation, not a fresh flow.
  const flowStartedRef = useRef(false);
  useEffect(() => {
    if (flowStartedRef.current || isReturnFlow) return;
    flowStartedRef.current = true;
    const entryPoint = (location.state as { entry_point?: string } | null)?.entry_point ?? 'direct';
    emit(ROLL_REVIEW_FLOW_STARTED, { entry_point: entryPoint });
  }, [isReturnFlow, location.state]);

  const instructorSelectedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!instructor?.id || isReturnFlow) return;
    if (instructorSelectedRef.current === instructor.id) return;
    instructorSelectedRef.current = instructor.id;
    emit(ROLL_REVIEW_INSTRUCTOR_SELECTED, {
      instructor_id: instructor.id,
      price_cents: derivedPriceCents || (instructor.price_cents ?? null),
      currency: derivedCurrency,
    });
  }, [instructor?.id, isReturnFlow, derivedPriceCents, derivedCurrency, instructor?.price_cents]);

  useEffect(() => {
    if (step !== 'checkout' || !instructor?.id) return;
    emit(PRICING_VIEWED, { instructor_id: instructor.id });
  }, [step, instructor?.id]);

  // Price breakdown for display (using detected price if available)
  const displayPriceCents = uploadedVideoId ? derivedPriceCents : DEFAULT_PRICE_CENTS;
  const displayCurrency = uploadedVideoId ? derivedCurrency : DEFAULT_CURRENCY;
  const breakdown = useRollReviewBreakdown(displayPriceCents, displayCurrency);

  const createPaymentIntent = useCreatePaymentIntent();

  if (!instructor) {
    if (profileLoading) {
      return (
        <SubPageLayout title={t('roll_review.request_title')}>
          <div className="flex items-center justify-center h-40">
            <Loader2 size={24} className="animate-spin text-white/40" />
          </div>
        </SubPageLayout>
      );
    }
    if (!isReturnFlow) {
      navigate('/browse-instructors', { replace: true });
    }
    return null;
  }

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handlePay = async () => {
    try {
      if (!uploadedVideoId) {
        toast({ title: t('roll_review.error_no_video_title'), description: t('roll_review.error_no_video'), variant: 'destructive' });
        return;
      }
      const result = await createPaymentIntent.mutateAsync({
        instructor_id:    instructor.id,
        training_video_id: uploadedVideoId,
        turnaround_hours: turnaroundHours,
        // Only meaningful for new-card flow; the saved-card flow uses an
        // already-attached PaymentMethod and ignores setup_future_usage.
        save_for_future:  saveForFuture && !savedCard,
      });
      setClientSecret(result.client_secret);
      setPaymentIntentStripeId(result.payment_intent_id);
      setPaymentRecordId(result.payment_id);
      setPaymentRollReviewId(result.roll_review_id);
      if (savedCard) setPaymentMode('saved');
      sessionStorage.setItem('rr_pending_instructor', JSON.stringify(instructor));
      sessionStorage.setItem('rr_pending_message', message);
      setStep('payment');
    } catch (error: unknown) {
      const code =
        error && typeof error === 'object' && 'code' in error && typeof (error as { code: unknown }).code === 'string'
          ? (error as { code: string }).code
          : undefined;

      // Sentry capture — observability for the most critical flow.
      // PREMIUM_GATE_ROLL_REVIEW and STRIPE_ACCOUNT_NOT_ACTIVE are expected
      // business-rule rejections and would be pure noise in the dashboard.
      // PRICING_NOT_CONFIGURED is an invariant breach: the browse-list
      // eligibility filter (see docs/roll-review-instructor-eligibility.md)
      // is supposed to keep practitioners away from unpriced instructors —
      // if this fires in prod, the filter has a hole.
      if (code === 'PRICING_NOT_CONFIGURED') {
        monitoring.captureException(error, {
          stage:        'create_payment_intent',
          code,
          instructorId: instructor.id,
        });
      } else if (code !== 'PREMIUM_GATE_ROLL_REVIEW' && code !== 'STRIPE_ACCOUNT_NOT_ACTIVE') {
        monitoring.captureException(error, {
          stage:        'create_payment_intent_unexpected',
          code:         code ?? null,
          instructorId: instructor.id,
        });
      }

      // Pre-row payment failure: PaymentIntent creation rejected the
      // request, so no payments / roll_reviews rows exist yet. This is the
      // gap PostHog owns; declines AFTER the PI is created are DB-derivable.
      emit(PAYMENT_FAILED_CLIENT, {
        reason: code ?? 'unexpected',
        service_type: 'roll_review',
      });

      const message_text =
        code === 'PRICING_NOT_CONFIGURED'
          ? t('roll_review.pricing_not_configured_body')
          : code === 'PREMIUM_GATE_ROLL_REVIEW'
            ? t('roll_review.premium_gate_body')
            : code === 'STRIPE_ACCOUNT_NOT_ACTIVE'
              ? t('roll_review.stripe_account_not_active_body')
              : error instanceof Error
                ? error.message
                : t('roll_review.payment_error_body');
      toast({
        title:
          code === 'PREMIUM_GATE_ROLL_REVIEW'
            ? t('roll_review.premium_gate_title')
            : code === 'PRICING_NOT_CONFIGURED'
              ? t('roll_review.pricing_not_configured_title')
              : code === 'STRIPE_ACCOUNT_NOT_ACTIVE'
                ? t('roll_review.stripe_account_not_active_title')
                : t('roll_review.payment_error_title'),
        description: message_text,
        variant: 'destructive',
      });
    }
  };

  const handlePaymentSuccess = async (confirmedPaymentIntentId: string) => {
    try {
      if (paymentRollReviewId && paymentRecordId) {
        await rollReviewApi.confirmPayment({
          paymentIntentId: confirmedPaymentIntentId,
          rollReviewId:    paymentRollReviewId,
          paymentId:       paymentRecordId,
        });
      }
    } catch (err) {
      console.warn('[handlePaymentSuccess] stripe-confirm-payment failed (non-fatal):', err);
    }

    sessionStorage.removeItem('rr_pending_instructor');
    sessionStorage.removeItem('rr_pending_message');
    sessionStorage.removeItem('rr_pending_video_id');
    sessionStorage.removeItem('rr_pending_video_path');
    sessionStorage.removeItem('rr_pending_duration_seconds');
    setStep('success');
  };

  const handlePayWithSavedCard = async () => {
    if (!savedCard || !paymentIntentStripeId || !paymentRecordId || !paymentRollReviewId) return;
    setIsPayingWithSavedCard(true);
    try {
      const result = await paymentMethodApi.payWithSavedMethod({
        paymentIntentId: paymentIntentStripeId,
        paymentMethodId: savedCard.id,
        paymentId: paymentRecordId,
        rollReviewId: paymentRollReviewId,
        instructorId: instructor.id,
      });

      if (result.status === 'succeeded') {
        await handlePaymentSuccess(paymentIntentStripeId);
        return;
      }

      toast({
        title: t('roll_review.payment_error_title'),
        description: `Saved card requires additional authentication (${result.status}). Please pay using a new card.`,
        variant: 'destructive',
      });
      setPaymentMode('new');
    } catch (error) {
      toast({
        title: t('roll_review.payment_error_title'),
        description: error instanceof Error ? error.message : t('roll_review.payment_error_body'),
        variant: 'destructive',
      });
      setPaymentMode('new');
    } finally {
      setIsPayingWithSavedCard(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    const formatValidation = validateVideoFile(file);
    if (!formatValidation.isValid) {
      toast({ title: t('roll_review.error_format_title'), description: formatValidation.error, variant: 'destructive' });
      return;
    }
    const durationValidation = await validateVideoDuration(file);
    if (!durationValidation.isValid) {
      toast({ title: t('roll_review.error_duration_title'), description: durationValidation.error, variant: 'destructive' });
      return;
    }
    setSelectedFile(file);
    if (durationValidation.durationSeconds) {
      setDetectedDurationSec(durationValidation.durationSeconds);
    }
  };

  // Reset local file selection without any I/O. The file input ref is also
  // cleared so picking the same file again still triggers `onChange`.
  const handleChangeFile = () => {
    setSelectedFile(null);
    setDetectedDurationSec(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Best-effort cleanup of the uploaded-but-unpaid video, then redirect back
  // to the browse list. Failure paths are non-blocking: orphans get swept by
  // the periodic cleanup routine (see docs/video-upload-recovery.md gap G2).
  const handleStartOver = async () => {
    if (uploadedVideoId) {
      await discardPendingVideo.mutateAsync({
        videoId:     uploadedVideoId,
        storagePath: uploadedVideoPath ?? '',
      });
    }
    sessionStorage.removeItem('rr_pending_instructor');
    sessionStorage.removeItem('rr_pending_message');
    sessionStorage.removeItem('rr_pending_video_id');
    sessionStorage.removeItem('rr_pending_video_path');
    sessionStorage.removeItem('rr_pending_duration_seconds');
    setShowStartOverDialog(false);
    navigate('/browse-instructors', { replace: true });
  };

  const estimateTimeRemaining = (progress: number): string => {
    if (!uploadStartTime || progress <= 0) return '';
    const elapsed = (Date.now() - uploadStartTime) / 1000;
    const rate = progress / elapsed;
    const remaining = (100 - progress) / rate;
    if (remaining < 60) return `${Math.ceil(remaining)}${t('roll_review.time_seconds_short')}`;
    return `${Math.ceil(remaining / 60)}${t('roll_review.time_minutes_short')}`;
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast({ title: t('roll_review.error_no_video_title'), description: t('roll_review.error_no_video'), variant: 'destructive' });
      return;
    }
    if (!pricing && !isReturnFlow) {
      toast({
        title: t('roll_review.pricing_not_configured_title'),
        description: t('roll_review.pricing_not_configured_body'),
        variant: 'destructive',
      });
      return;
    }

    setStep('uploading');
    setUploadProgress(0);
    setUploadStartTime(Date.now());

    const title = `Roll Review - ${instructor.full_name} - ${format(new Date(), 'MMM d, yyyy')}`;

    const result = await uploadTrainingVideo({
      file:        selectedFile,
      title,
      description: message.trim() || undefined,
      reviewerId:  instructor.id,
      durationSeconds: detectedDurationSec ?? undefined,
      onProgress:  (p) => setUploadProgress(p),
      context:     'roll_review',
    });

    if (!result.success) {
      setStep('upload');
      toast({ title: t('roll_review.error_upload_title'), description: result.error ?? t('roll_review.error_upload_retry'), variant: 'destructive' });
      return;
    }

    // Return flow (status=paid): link video directly to the paid roll_review
    if (isReturnFlow && returnRollReviewId && result.video) {
      await rollReviewApi.linkTrainingVideoToPaidReview(returnRollReviewId, result.video.id);
      setStep('success');
      return;
    }

    if (result.video && detectedDurationSec !== null) {
      setUploadedVideoId(result.video.id);
      setUploadedVideoPath(result.video.video_url);
      // Price/currency are derived from pricing + detectedDurationSec at render
      // time, so no setters needed here.
      sessionStorage.setItem('rr_pending_video_id', result.video.id);
      sessionStorage.setItem('rr_pending_video_path', result.video.video_url);
      sessionStorage.setItem('rr_pending_duration_seconds', String(detectedDurationSec));
      setStep('checkout');
    } else {
      toast({ title: 'Error', description: 'Could not determine video duration', variant: 'destructive' });
      setStep('upload');
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <SubPageLayout title={t('roll_review.request_title')} hideBottomNav={step !== 'payment'}>

      {/* ── CHECKOUT STEP ───────────────────────────────────────────────── */}
      {step === 'checkout' && (
        <>
          <div className="space-y-5 pb-32">

            {/* Instructor card */}
            <div className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl p-4">
              <FramedAvatar
                src={instructor.profile_picture_url}
                alt={instructor.full_name}
                initials={getInitials(instructor.full_name)}
                beltRank={parseBeltColor(instructor.belt_rank)}
                frameSize={{ w: 52, h: 58 }}
                isManual
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">{instructor.full_name}</p>
                <p className="text-xs text-white/50">
                  {getBeltLabel(instructor.belt_rank, t)}{' '}
                  · {t('roll_review.responds_in', { time: instructor.avg_response_time ?? '12h' })}
                </p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-lg font-bold">{breakdown.amountFormatted}</p>
                <p className="text-xs text-white/40">{t('roll_review.per_review')}</p>
              </div>
            </div>

            {/* Detected tier + pricing table */}
            {pricing && uploadedVideoId && (
              <TierPricingTable
                pricing={pricing}
                highlightedTierKey={detectedDurationSec !== null ? detectTierFromDurationSeconds(detectedDurationSec) : undefined}
              />
            )}

            {/* Price breakdown */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-2">
              <p className="text-xs font-semibold text-white/50 tracking-widest mb-3">
                {t('roll_review.payment_breakdown')}
              </p>
              <div className="flex justify-between text-sm">
                <span className="text-white/70">{t('roll_review.review_line')}</span>
                <span className="font-medium">{breakdown.amountFormatted}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-white/50 flex items-center gap-1">
                  {t('roll_review.service_fee')}
                  <span title={t('roll_review.service_fee_tooltip')}>
                    <Info size={11} className="text-white/30 cursor-help" />
                  </span>
                </span>
                <span className="text-white/50">+{breakdown.serviceFeeFormatted}</span>
              </div>
              <div className="border-t border-white/10 pt-2 flex justify-between text-sm font-semibold">
                <span>{t('roll_review.total_charged')}</span>
                <span>{breakdown.totalChargedFormatted}</span>
              </div>
            </div>

            {/* Optional message */}
            <div>
              <p className="text-xs font-semibold text-white/50 tracking-widest mb-2">
                {t('roll_review.focus_label')}
              </p>
              <div className="relative">
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value.slice(0, MAX_MESSAGE_CHARS))}
                  placeholder={t('roll_review.focus_placeholder')}
                  rows={4}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-white/30 resize-none transition-colors"
                />
                <span className="absolute bottom-3 right-4 text-xs text-white/30">
                  {message.length}/{MAX_MESSAGE_CHARS}
                </span>
              </div>
            </div>

            {/* Turnaround hint */}
            <div className="flex items-center gap-2 text-white/40">
              <Clock size={14} />
              <p className="text-xs">
                {t('roll_review.turnaround_hint', { hours: turnaroundHours })}
              </p>
            </div>

            {/* SOS-336 — Save card toggle (only when user has no saved card) */}
            {!savedCardLoading && !savedCard && (
              <label className="flex items-start gap-3 cursor-pointer select-none rounded-xl border border-white/10 bg-white/5 px-4 py-3">
                <input
                  type="checkbox"
                  checked={saveForFuture}
                  onChange={(e) => setSaveForFuture(e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-[#D72E16]"
                />
                <span className="flex-1">
                  <span className="block text-sm text-white/90">{t('roll_review.save_for_future')}</span>
                  <span className="block text-xs text-white/40 mt-0.5">{t('roll_review.save_for_future_hint')}</span>
                </span>
              </label>
            )}
          </div>

          {/* Pay button */}
          <div className="fixed bottom-0 inset-x-0 px-4 pb-8 pt-4 bg-gradient-to-t from-sossiety-dark to-transparent flex flex-col gap-2">
            <Button
              variant="primary"
              size="lg"
              onClick={handlePay}
              disabled={createPaymentIntent.isPending}
              className="w-full"
            >
              {createPaymentIntent.isPending ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  {t('roll_review.proceeding_to_payment')}
                </>
              ) : (
                <>
                  <CreditCard size={16} className="mr-2" />
                  {t('roll_review.pay_and_request', { amount: breakdown.totalChargedFormatted })}
                </>
              )}
            </Button>
            <button
              type="button"
              onClick={() => setShowStartOverDialog(true)}
              className="text-center text-sm text-white/60 hover:text-white transition-colors underline-offset-2 hover:underline py-1"
            >
              {t('roll_review.start_over_label')}
            </button>
          </div>
        </>
      )}

      {/* ── PAYMENT STEP ──────────────────────────────────────────────── */}
      {step === 'payment' && (
        <>
          {/* Non-destructive escape hatch: returns to the price summary so the
              practitioner can re-read the breakdown without abandoning the
              already-created roll_reviews row (which would orphan a
              payment_intent — see docs/video-upload-recovery.md). */}
          <button
            type="button"
            onClick={() => setStep('checkout')}
            className="mb-3 inline-flex items-center gap-1 text-sm text-white/70 hover:text-white transition-colors"
          >
            ← {t('roll_review.back_to_summary_label')}
          </button>

          {/* 3-Column Layout (Desktop) / Stacked Layout (Mobile/Tablet) */}
          {isDesktop ? (
            <div className="grid grid-cols-3 gap-4 pb-20 h-[calc(100vh-200px)]">

              {/* LEFT COLUMN: Instructor List */}
              <div className="overflow-y-auto space-y-3 pr-2">
                <p className="text-xs font-semibold text-white/50 tracking-widest sticky top-0 bg-sossiety-dark py-2">
                  {t('roll_review.find_instructors')}
                </p>

                {/* Current instructor */}
                <div className="flex items-center gap-3 bg-white/10 border border-white/30 rounded-lg p-3 sticky top-10">
                  <FramedAvatar
                    src={instructor.profile_picture_url}
                    alt={instructor.full_name}
                    initials={getInitials(instructor.full_name)}
                    beltRank={parseBeltColor(instructor.belt_rank)}
                    frameSize={{ w: 40, h: 46 }}
                    isManual
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold truncate">{instructor.full_name}</p>
                    <p className="text-xs text-white/50">{getBeltLabel(instructor.belt_rank, t)}</p>
                    <p className="text-xs text-green-400 font-medium">{breakdown.amountFormatted}</p>
                  </div>
                </div>
              </div>

              {/* MIDDLE COLUMN: Summary */}
              <div className="overflow-y-auto space-y-4 px-2">
                {/* Instructor card */}
                <div className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl p-4">
                  <FramedAvatar
                    src={instructor.profile_picture_url}
                    alt={instructor.full_name}
                    initials={getInitials(instructor.full_name)}
                    beltRank={parseBeltColor(instructor.belt_rank)}
                    frameSize={{ w: 52, h: 58 }}
                    isManual
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{instructor.full_name}</p>
                    <p className="text-xs text-white/50">
                      {getBeltLabel(instructor.belt_rank, t)}{' '}
                      · {t('roll_review.responds_in', { time: instructor.avg_response_time ?? '12h' })}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-lg font-bold">{breakdown.amountFormatted}</p>
                    <p className="text-xs text-white/40">{t('roll_review.per_review')}</p>
                  </div>
                </div>

                {/* Price breakdown */}
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-2">
                  <p className="text-xs font-semibold text-white/50 tracking-widest mb-3">
                    {t('roll_review.payment_breakdown')}
                  </p>
                  <div className="flex justify-between text-sm">
                    <span className="text-white/70">{t('roll_review.review_line')}</span>
                    <span className="font-medium">{breakdown.amountFormatted}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-white/50 flex items-center gap-1">
                      {t('roll_review.service_fee')}
                      <span title={t('roll_review.service_fee_tooltip')}>
                        <Info size={11} className="text-white/30 cursor-help" />
                      </span>
                    </span>
                    <span className="text-white/50">+{breakdown.serviceFeeFormatted}</span>
                  </div>
                  <div className="border-t border-white/10 pt-2 flex justify-between text-sm font-semibold">
                    <span>{t('roll_review.total_charged')}</span>
                    <span>{breakdown.totalChargedFormatted}</span>
                  </div>
                </div>
              </div>

              {/* RIGHT COLUMN: Payment Form */}
              <div className="overflow-y-auto pr-2">
                <div className="space-y-3 mb-4">
                  <p className="text-xs font-semibold tracking-widest text-white/50">{t('roll_review.payment_method')}</p>
                  <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                    <div className="flex flex-col gap-2">
                      {savedCardLoading ? (
                        <div className="flex items-center gap-2 text-white/60 text-sm">
                          <Loader2 size={14} className="animate-spin" />
                          {t('roll_review.loading_saved_card')}
                        </div>
                      ) : savedCard ? (
                        <button
                          type="button"
                          onClick={() => setPaymentMode('saved')}
                          className={`text-left px-3 py-2 rounded-lg border text-sm ${
                            paymentMode === 'saved' ? 'border-[#D72E16] bg-[#D72E16]/10' : 'border-white/10'
                          }`}
                        >
                          {t('roll_review.use_saved_card', { brand: savedCard.brand.toUpperCase(), last4: savedCard.last4 })}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => setPaymentMode('new')}
                        className={`text-left px-3 py-2 rounded-lg border text-sm ${
                          paymentMode === 'new' ? 'border-[#D72E16] bg-[#D72E16]/10' : 'border-white/10'
                        }`}
                      >
                        {t('roll_review.use_new_card')}
                      </button>
                    </div>
                  </div>
                </div>

                {paymentMode === 'saved' && savedCard ? (
                  <div className="rounded-xl border border-white/10 bg-white/5 p-4 space-y-3">
                    <p className="text-sm text-white/80">
                      Paying with {savedCard.brand.toUpperCase()} •••• {savedCard.last4}
                    </p>
                    <Button
                      variant="primary"
                      size="lg"
                      className="w-full"
                      onClick={handlePayWithSavedCard}
                      disabled={isPayingWithSavedCard}
                    >
                      {isPayingWithSavedCard ? (
                        <>
                          <Loader2 size={14} className="mr-2 animate-spin" />
                          {t('roll_review.processing')}
                        </>
                      ) : (
                        t('roll_review.pay_and_request', { amount: breakdown.totalChargedFormatted })
                      )}
                    </Button>
                  </div>
                ) : (
                  <StripePaymentElementWrapper
                    instructorId={instructor.id}
                    rollReviewId={paymentRollReviewId}
                    clientSecret={clientSecret}
                    amount={breakdown.totalChargedCents}
                    currency={displayCurrency}
                    instructorName={instructor.full_name}
                    breakdown={{
                      amount_cents: displayPriceCents,
                      platform_fee_cents: breakdown.platformFeeCents,
                      net_to_instructor_cents: breakdown.netToInstructorCents,
                      currency: displayCurrency,
                    }}
                    onSuccess={handlePaymentSuccess}
                    onError={(error: string) => {
                      toast({
                        title: t('roll_review.payment_error_title'),
                        description: error,
                        variant: 'destructive',
                      });
                      setStep('checkout');
                    }}
                  />
                )}
              </div>
            </div>
          ) : (
            /* Fallback: Stacked Layout (Mobile/Tablet) */
            <div className="space-y-5 pb-32">
              {/* Instructor card */}
              <div className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl p-4">
                <FramedAvatar
                  src={instructor.profile_picture_url}
                  alt={instructor.full_name}
                  initials={getInitials(instructor.full_name)}
                  beltRank={parseBeltColor(instructor.belt_rank)}
                  frameSize={{ w: 52, h: 58 }}
                  isManual
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{instructor.full_name}</p>
                  <p className="text-xs text-white/50">
                    {getBeltLabel(instructor.belt_rank, t)}{' '}
                    · {t('roll_review.responds_in', { time: instructor.avg_response_time ?? '12h' })}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-lg font-bold">{breakdown.amountFormatted}</p>
                  <p className="text-xs text-white/40">{t('roll_review.per_review')}</p>
                </div>
              </div>

              {/* Price breakdown */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-2">
                <p className="text-xs font-semibold text-white/50 tracking-widest mb-3">
                  {t('roll_review.payment_breakdown')}
                </p>
                <div className="flex justify-between text-sm">
                  <span className="text-white/70">{t('roll_review.review_line')}</span>
                  <span className="font-medium">{breakdown.amountFormatted}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-white/50 flex items-center gap-1">
                    {t('roll_review.service_fee')}
                    <span title={t('roll_review.service_fee_tooltip')}>
                      <Info size={11} className="text-white/30 cursor-help" />
                    </span>
                  </span>
                  <span className="text-white/50">+{breakdown.serviceFeeFormatted}</span>
                </div>
                <div className="border-t border-white/10 pt-2 flex justify-between text-sm font-semibold">
                  <span>{t('roll_review.total_charged')}</span>
                  <span>{breakdown.totalChargedFormatted}</span>
                </div>
              </div>

              <div className="space-y-3">
                <p className="text-xs font-semibold tracking-widest text-white/50">{t('roll_review.payment_method')}</p>
                <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                  <div className="flex flex-col gap-2">
                    {savedCardLoading ? (
                      <div className="flex items-center gap-2 text-white/60 text-sm">
                        <Loader2 size={14} className="animate-spin" />
                        {t('roll_review.loading_saved_card')}
                      </div>
                    ) : savedCard ? (
                      <button
                        type="button"
                        onClick={() => setPaymentMode('saved')}
                        className={`text-left px-3 py-2 rounded-lg border text-sm ${
                          paymentMode === 'saved' ? 'border-[#D72E16] bg-[#D72E16]/10' : 'border-white/10'
                        }`}
                      >
                        {t('roll_review.use_saved_card', { brand: savedCard.brand.toUpperCase(), last4: savedCard.last4 })}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => setPaymentMode('new')}
                      className={`text-left px-3 py-2 rounded-lg border text-sm ${
                        paymentMode === 'new' ? 'border-[#D72E16] bg-[#D72E16]/10' : 'border-white/10'
                      }`}
                    >
                      {t('roll_review.use_new_card')}
                    </button>
                  </div>
                </div>

                {paymentMode === 'saved' && savedCard ? (
                  <div className="rounded-xl border border-white/10 bg-white/5 p-4 space-y-3">
                    <p className="text-sm text-white/80">
                      Paying with {savedCard.brand.toUpperCase()} •••• {savedCard.last4}
                    </p>
                    <Button
                      variant="primary"
                      size="lg"
                      className="w-full"
                      onClick={handlePayWithSavedCard}
                      disabled={isPayingWithSavedCard}
                    >
                      {isPayingWithSavedCard ? (
                        <>
                          <Loader2 size={14} className="mr-2 animate-spin" />
                          {t('roll_review.processing')}
                        </>
                      ) : (
                        t('roll_review.pay_and_request', { amount: breakdown.totalChargedFormatted })
                      )}
                    </Button>
                  </div>
                ) : (
                  <StripePaymentElementWrapper
                    instructorId={instructor.id}
                    rollReviewId={paymentRollReviewId}
                    clientSecret={clientSecret}
                    amount={breakdown.totalChargedCents}
                    currency={displayCurrency}
                    instructorName={instructor.full_name}
                    breakdown={{
                      amount_cents: displayPriceCents,
                      platform_fee_cents: breakdown.platformFeeCents,
                      net_to_instructor_cents: breakdown.netToInstructorCents,
                      currency: displayCurrency,
                    }}
                    onSuccess={handlePaymentSuccess}
                    onError={(error: string) => {
                      toast({
                        title: t('roll_review.payment_error_title'),
                        description: error,
                        variant: 'destructive',
                      });
                      setStep('checkout');
                    }}
                  />
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* ── UPLOAD STEP (post-payment) ───────────────────────────────────── */}
      {step === 'upload' && (
        <>
          <div className="space-y-5 pb-32">

            {/* Return-from-Stripe banner */}
            {returnStatus === 'paid' && (
              <div className="rounded-xl border border-green-700/40 bg-green-900/20 p-4 space-y-1">
                <p className="text-sm font-semibold text-green-300">
                  {t('roll_review.payment_confirmed_title')}
                </p>
                <p className="text-xs text-green-400/80">
                  {t('roll_review.payment_confirmed_body')}
                </p>
              </div>
            )}
            {returnStatus === 'cancelled' && (
              <div className="rounded-xl border border-yellow-700/40 bg-yellow-900/20 p-4 space-y-1">
                <p className="text-sm font-semibold text-yellow-300">
                  {t('roll_review.payment_cancelled_title')}
                </p>
                <p className="text-xs text-yellow-400/80">
                  {t('roll_review.payment_cancelled_body')}
                </p>
              </div>
            )}

            {/* Instructor card */}
            <div className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl p-4">
              <FramedAvatar
                src={instructor.profile_picture_url}
                alt={instructor.full_name}
                initials={getInitials(instructor.full_name)}
                beltRank={parseBeltColor(instructor.belt_rank)}
                frameSize={{ w: 52, h: 58 }}
                isManual
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">{instructor.full_name}</p>
                <p className="text-xs text-white/50">
                  {getBeltLabel(instructor.belt_rank, t)}
                </p>
              </div>
            </div>

            {/* Upload hint */}
            <p className="text-xs text-white/50">
              {t('roll_review.upload_first_hint')}
            </p>

            {/* Pricing not configured guard */}
            {!pricing ? (
              <div className="rounded-xl border border-yellow-700/40 bg-yellow-900/20 p-4 space-y-1">
                <p className="text-sm font-semibold text-yellow-300">
                  {t('roll_review.pricing_not_configured_title')}
                </p>
                <p className="text-xs text-yellow-400/80">
                  {t('roll_review.pricing_not_configured_body')}
                </p>
              </div>
            ) : (
              <TierPricingTable pricing={pricing} />
            )}

            {/* Video upload area */}
            <div>
              <p className="text-xs font-semibold text-white/50 tracking-widest mb-2">
                {t('roll_review.upload_your_video')}
              </p>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className={`w-full flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed py-10 px-4 transition-colors ${
                  selectedFile
                    ? 'border-white/30 bg-white/5'
                    : 'border-white/15 bg-transparent hover:border-white/30 hover:bg-white/5'
                }`}
              >
                <Upload size={28} className="text-white/60" />
                {selectedFile ? (
                  <>
                    <p className="text-sm font-medium text-white">{selectedFile.name}</p>
                    <p className="text-xs text-white/50">{formatBytes(selectedFile.size)}</p>
                    {detectedDurationSec !== null && (
                      <p className="text-xs text-white/40">
                        {t('roll_review.tier_detected_duration', {
                          duration: `${Math.floor(detectedDurationSec / 60)}:${String(detectedDurationSec % 60).padStart(2, '0')}`,
                        })}
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <p className="text-sm font-semibold text-white">{t('roll_review.upload_title')}</p>
                    <p className="text-xs text-white/40">{t('roll_review.upload_subtitle')}</p>
                  </>
                )}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="video/mp4,video/quicktime,video/webm"
                className="hidden"
                onChange={handleFileChange}
              />

              {selectedFile && (
                <button
                  type="button"
                  onClick={handleChangeFile}
                  className="mt-3 w-full text-center text-sm text-white/60 hover:text-white transition-colors underline-offset-2 hover:underline"
                >
                  {t('roll_review.change_file_label')}
                </button>
              )}
            </div>
          </div>

          <div className="fixed bottom-0 inset-x-0 px-4 pb-8 pt-4 bg-gradient-to-t from-sossiety-dark to-transparent">
            <Button
              variant="primary"
              size="lg"
              onClick={handleUpload}
              disabled={!selectedFile || (!pricing && !isReturnFlow)}
              className="w-full"
            >
              {t('roll_review.submit_button')}
            </Button>
          </div>
        </>
      )}

      {/* ── UPLOADING STEP ──────────────────────────────────────────────── */}
      {step === 'uploading' && (
        <div className="flex-1 flex flex-col items-center justify-center gap-6 pb-28">
          <div className="flex flex-col items-center gap-4 w-full">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center"
              style={{ backgroundColor: `${SOSSIETY_RED}33` }}
            >
              <Upload size={28} style={{ color: SOSSIETY_RED }} />
            </div>

            <p className="text-base font-semibold">
              {t('roll_review.uploading_label')}
            </p>

            {selectedFile && (
              <p className="text-xs text-white/50">
                {selectedFile.name} · {formatBytes(selectedFile.size)}
              </p>
            )}

            <div className="w-full bg-white/10 rounded-full h-2">
              <div
                className="h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%`, backgroundColor: SOSSIETY_RED }}
              />
            </div>

            <p className="text-xs text-white/50">
              {uploadProgress}%
              {uploadStartTime ? ` · ${estimateTimeRemaining(uploadProgress)}` : ''}
            </p>
          </div>
        </div>
      )}

      {/* ── SUCCESS STEP ────────────────────────────────────────────────── */}
      {step === 'success' && (
        <>
          <div className="flex flex-col items-center text-center gap-5 pt-16 pb-40">
            <div className="w-20 h-20 rounded-full bg-green-900/40 flex items-center justify-center">
              <CheckCircle2 size={40} className="text-green-400" />
            </div>
            <p className="text-2xl font-bold">{t('roll_review.request_sent_title')}</p>
            <p className="text-sm text-white/60 max-w-xs">
              {t('roll_review.request_sent_body', { instructor_name: instructor.full_name })}
            </p>
          </div>

          {/* Solid bottom container — prevents content bleed-through behind the CTAs */}
          <div className="fixed bottom-0 inset-x-0 px-4 pb-8 pt-6 bg-sossiety-dark border-t border-white/[0.06] flex flex-col gap-3">
            <Button variant="primary" size="lg" onClick={() => navigate('/my-reviews')} className="w-full">
              {t('roll_review.view_reviews_button')}
            </Button>
            <Button variant="outline" size="lg" onClick={() => navigate('/home')} className="w-full border-white/30 hover:border-white/50">
              {t('roll_review.back_home_button')}
            </Button>
          </div>
        </>
      )}

      {/* Start-over confirmation dialog (used by the checkout step) */}
      <AlertDialog open={showStartOverDialog} onOpenChange={setShowStartOverDialog}>
        <AlertDialogContent className="w-[90vw] max-w-sm bg-white rounded-2xl p-6 shadow-xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[17px] font-semibold text-gray-900">
              {t('roll_review.start_over_confirm_title')}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[13px] text-gray-500 leading-relaxed">
              {t('roll_review.start_over_confirm_body')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="!flex-col !justify-start !space-x-0 gap-2 mt-4">
            <AlertDialogAction
              onClick={handleStartOver}
              disabled={discardPendingVideo.isPending}
              className="h-11 w-full rounded-full border-0 font-medium text-white bg-sossiety-red hover:bg-sossiety-red-hover disabled:opacity-50"
            >
              {discardPendingVideo.isPending ? (
                <>
                  <Loader2 size={14} className="mr-2 animate-spin" />
                  {t('roll_review.processing')}
                </>
              ) : (
                t('roll_review.start_over_confirm_action')
              )}
            </AlertDialogAction>
            <AlertDialogCancel className="mt-0 h-11 w-full rounded-full border-0 font-medium bg-[#F0F0F0] text-gray-900 hover:bg-gray-200">
              {t('common.cancel')}
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SubPageLayout>
  );
};

export default RequestRollReview;
